// export type ModalFullScreen = Exclude<Breakpoint, 'xs'> | 'always';

import classnames from 'classnames';
import React from 'react';

import { Button } from './Button';
import { appConfig } from './config';
import { ModalContext, ModalContextInterface, useModalContext } from './ModalContext';
import { useModals } from './useModals';
import { createUUID, renderUI, wait } from './util';
import { PropsWithCustomComponent, VariantsController } from './VariantsController';
export type OffcanvasPlacement = 'left' | 'right' | 'top' | 'bottom';

const focusableEl = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');
export interface OffcanvasProps {
  autoClose?: number; // in seconds
  backdrop?: boolean;
  children?: React.ReactNode | ((modalContext: ModalContextInterface) => React.ReactNode);
  className?: string;
  focusTrap?: boolean;
  innerRef?: React.RefObject<HTMLDivElement>;
  onClose?(): void;
  placement?: OffcanvasPlacement;
  scroll?: boolean;
}

export const Offcanvas = (props: OffcanvasProps) => {
  const id = React.useMemo(() => createUUID(), []);
  return <OffcanvasWithId {...props} id={id} />;
};

export interface OffcanvasWithIdProps extends OffcanvasProps {
  id: string;
}

export const OffcanvasWithId = ({
  autoClose,
  backdrop = true,
  children,
  className,
  focusTrap = true,
  id,
  innerRef,
  onClose,
  placement = 'left',
  scroll,
}: OffcanvasWithIdProps) => {
  const { addEventListener, close } = useModals();

  React.useEffect(() => {
    onClose && addEventListener(id, 'hidden', 'offcanvas', onClose);
  }, [addEventListener, id, onClose]);

  React.useEffect(() => {
    autoClose &&
      addEventListener(id, 'shown', 'offcanvas', () => {
        wait(autoClose * 1000).then(() => {
          close(id);
        });
      });
  }, [addEventListener, autoClose, close, id, onClose]);

  React.useEffect(() => {
    if (!focusTrap) return;

    let previouslyFocused: HTMLElement | null = null;

    const getFocusableElements = (offcanvas: HTMLElement): Array<HTMLElement> =>
      Array.from(offcanvas.querySelectorAll<HTMLElement>(focusableEl)).filter((el) => !el.dataset.modalSentinel);

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const offcanvas = document.getElementById(id);
      if (!offcanvas || !offcanvas.classList.contains('show')) return;

      const focusable = getFocusableElements(offcanvas);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!offcanvas.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey) {
        if (active === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    const handleOffcanvasShow = () => {
      previouslyFocused = document.activeElement as HTMLElement;
      document.addEventListener('keydown', trapFocus as EventListener);

      const offcanvas = document.getElementById(id);
      if (!offcanvas) return;

      const sentinelStart = document.createElement('div');
      sentinelStart.setAttribute('tabindex', '0');
      sentinelStart.setAttribute('aria-hidden', 'true');
      sentinelStart.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
      sentinelStart.dataset.modalSentinel = 'start';

      const sentinelEnd = document.createElement('div');
      sentinelEnd.setAttribute('tabindex', '0');
      sentinelEnd.setAttribute('aria-hidden', 'true');
      sentinelEnd.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
      sentinelEnd.dataset.modalSentinel = 'end';

      offcanvas.insertBefore(sentinelStart, offcanvas.firstChild);
      offcanvas.appendChild(sentinelEnd);

      sentinelStart.addEventListener('focus', () => {
        const focusable = getFocusableElements(offcanvas);
        focusable[focusable.length - 1]?.focus();
      });

      sentinelEnd.addEventListener('focus', () => {
        const focusable = getFocusableElements(offcanvas);
        focusable[0]?.focus();
      });

      requestAnimationFrame(() => {
        const focusable = getFocusableElements(offcanvas);
        focusable[0]?.focus();
      });
    };

    const handleOffcanvasHide = () => {
      document.removeEventListener('keydown', trapFocus as EventListener);
      document.querySelectorAll('[data-modal-sentinel]').forEach((el) => el.remove());
      previouslyFocused?.focus();
      previouslyFocused = null;
    };

    const removeShowListener = addEventListener(id, 'shown', 'offcanvas', handleOffcanvasShow);
    const removeHideListener = addEventListener(id, 'hidden', 'offcanvas', handleOffcanvasHide);

    return () => {
      removeShowListener();
      removeHideListener();
      document.removeEventListener('keydown', trapFocus as EventListener);
      document.querySelectorAll('[data-modal-sentinel]').forEach((el) => el.remove());
    };
  }, [focusTrap, id, addEventListener]);

  return (
    <ModalContext id={id}>
      {(modalContext) =>
        renderUI({
          bs5: (
            <div
              aria-labelledby="offcanvasLabel"
              className={classnames(
                'offcanvas',
                { 'offcanvas-start': placement === 'left' },
                { 'offcanvas-end': placement === 'right' },
                { 'offcanvas-top': placement === 'top' },
                { 'offcanvas-bottom': placement === 'bottom' },
                className,
              )}
              data-bs-backdrop={backdrop}
              data-bs-scroll={scroll}
              id={id}
              ref={innerRef}
              tabIndex={-1}
            >
              {typeof children === 'function' ? children(modalContext) : children}
            </div>
          ),
        })
      }
    </ModalContext>
  );
};

export interface PackedOffcanvasProps extends OffcanvasWithIdProps {
  title: string;
}

export interface OffcanvasHeaderProps {
  children?: React.ReactNode;
  className?: string;
  smallPadding?: boolean;
  title?: string;
}

const OffcanvasHeader = ({ children, className, title }: OffcanvasHeaderProps) => {
  const { id } = useModalContext();
  return renderUI({
    bs5: (
      <div className={classnames('offcanvas-header', className)}>
        {(title || children) && (
          <h5 className="offcanvas-title" id={`${id}-label`}>
            {title}
            {children}
          </h5>
        )}
        <Button
          aria-label="Close"
          data-bs-dismiss="offcanvas"
          iconEnd={appConfig.icons ? { name: appConfig.icons.close } : undefined}
          type="button"
          variant="blank"
        />
      </div>
    ),
  });
};

export interface OffcanvasBodyProps {
  children?: React.ReactNode;
  className?: string;
}

export const OffcanvasBody = ({ children, className }: OffcanvasBodyProps) => {
  return renderUI({
    bs5: <div className={classnames('offcanvas-body', className)}>{children}</div>,
  });
};

export type OffcanvasHeaderVariants = '';

const OffcanvasHeaderController = (props: PropsWithCustomComponent<OffcanvasHeaderProps>) => (
  <VariantsController<OffcanvasHeaderProps, OffcanvasHeaderVariants>
    {...props}
    variantsControllerConfig={{
      defaultComponent: OffcanvasHeader,
      name: 'OffcanvasHeader',
    }}
  />
);
export { OffcanvasHeaderController as OffcanvasHeader };
